import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthUser, useAuthActions } from '../context/authHooks';
import { useSettings } from '../context/SettingsContext';
import {
  Home, Database, File, Folder, Settings, User, Power, Calendar, CheckSquare, Plus,
  Briefcase, Lock, Users, CreditCard, Bell, Link as LinkIcon, Zap, Cpu, ClipboardList, X,
  MessageCircle, Mail, Grid, Search, AppWindow, Trash2, Pickaxe
} from 'lucide-react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import './Menu1.css';
import WhatsPanel from '../features/settings/components/dapps-panel/whatsapp/WhatsPanel';
import { useCurrentUserPermissions } from '../features/spaces/hooks/useSpaceDashboard';
import { useShellStore } from '../shared/store/useShellStore';



const Menu1 = () => {
  const { t } = useTranslation();
  const user = useAuthUser();
  const { logout, updateUser } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const { closePanel, activeSetting, setActiveSetting } = useSettings();
  const { permissions, role: spaceRole } = useCurrentUserPermissions();
  /* State */
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  // Integración de los avances de main (ver .claude/plans/tech-debt): switcher de espacios —
  // `user.currentSpace` ya existía en UserContext (con persistencia vía `updateUser`), sin UI
  // para cambiarlo. `user.spaces` (normalizado por UserContext) solo trae `{admin, member}`
  // como ids — a diferencia de main, acá se resuelve la lista completa (nombre/ícono) vía el
  // mismo endpoint que ya usa `Home.jsx` para el dashboard de espacios.
  const [showSpacesDropdown, setShowSpacesDropdown] = useState(false);
  const [spacesList, setSpacesList] = useState([]);

  useEffect(() => {
    if (!user?.dbId) return;
    fetch(`/api/user/${user.dbId}/spaces`)
      .then(res => res.json())
      .then(data => { if (data.success) setSpacesList(data.spaces); })
      .catch(err => console.error('Error fetching spaces for switcher:', err));
  }, [user?.dbId]);
  
  const [showTodo, setShowTodo] = useState(false);
  const [showWhats, setShowWhats] = useState(() => window.location.hash.startsWith('#whats'));
  
  // Listen for hash changes to open WhatsApp panel
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash.startsWith('#whats')) {
        setShowWhats(true);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  const [todoBlocks, setTodoBlocks] = useState([{ id: Date.now().toString(), text: '', done: false, tab_id: 'general' }]);
  const [todoTabs, setTodoTabs] = useState([]);
  const [editingTabId, setEditingTabId] = useState(null);
  const isInitialLoad = useRef(true);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (!user || !user.id) return;
    
    const stored = localStorage.getItem('colonny_user_todo');
    
    fetch(`/api/users/${user.id}/todos`)
        .then(res => res.json())
        .then(data => {
            if (data.success) {
                if (data.todos && data.todos.length > 0) {
                    setTodoBlocks(data.todos);
                }
                if (data.tabs && data.tabs.length > 0) {
                    setTodoTabs(data.tabs);
                }
                if (stored) localStorage.removeItem('colonny_user_todo');
            } else if (stored) {
                try {
                    let blocks = [];
                    if (stored.startsWith('[')) {
                        blocks = JSON.parse(stored);
                    } else {
                        const lines = stored.split('\n');
                        blocks = lines.map((line, index) => ({
                            id: Date.now().toString() + '-' + index,
                            text: line,
                            done: false,
                            tab_id: 'general'
                        }));
                    }
                    if (blocks.length > 0) {
                        setTodoBlocks(blocks);
                        fetch(`/api/users/${user.id}/todos/batch`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ todos: blocks, tabs: [] })
                        }).then(() => {
                            localStorage.removeItem('colonny_user_todo');
                        });
                    }
                } catch (e) {
                    console.error("Migration error:", e);
                }
            }
            isInitialLoad.current = false;
        })
        .catch(err => {
            console.error("Error fetching todos:", err);
            isInitialLoad.current = false;
        });
  }, [user]);

  useEffect(() => {
    if (isInitialLoad.current || !user || !user.id) return;
    
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
        fetch(`/api/users/${user.id}/todos/batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ todos: todoBlocks, tabs: todoTabs })
        }).catch(err => console.error("Error saving todos:", err));
    }, 1000);
    
    return () => clearTimeout(saveTimeoutRef.current);
  }, [todoBlocks, todoTabs, user]);

  const [activeBlockId, setActiveBlockId] = useState(null);
  const [draggedBlockId, setDraggedBlockId] = useState(null);
  const [todoTab, setTodoTab] = useState('activo');
  const [todoSearch, setTodoSearch] = useState('');

  const adjustTodoHeight = (el) => {
    if (!el) return;
    const container = el.closest('.todo-panel-content');
    const containerScrollTop = container ? container.scrollTop : 0;
    
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    
    if (container) {
      container.scrollTop = containerScrollTop;
    }
  };

  useEffect(() => {
    if (showTodo) {
      setTimeout(() => {
        document.querySelectorAll('.todo-block-input').forEach(adjustTodoHeight);
      }, 50);
    }
  }, [showTodo, todoTab, todoBlocks]);

  const handleBlockChange = (id, newText) => {
      setTodoBlocks(prev => prev.map(b => b.id === id ? { ...b, text: newText } : b));
  };

  const handleBlockKeyDown = (e, blockId) => {
      const index = todoBlocks.findIndex(b => b.id === blockId);
      if (index === -1) return;

      if (e.key === 'Enter') {
          if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) {
              return; // let the textarea insert a newline naturally
          }
          e.preventDefault();
          const targetTab = (todoTab === 'activo' || todoTab === 'hecho') ? 'general' : todoTab;
          const newBlock = { id: Date.now().toString(), text: '', done: false, tab_id: targetTab };
          setTodoBlocks(prev => {
              const newBlocks = [...prev];
              newBlocks.splice(index + 1, 0, newBlock);
              return newBlocks;
          });
          setTimeout(() => {
              const nextEl = document.getElementById(`todo-block-${newBlock.id}`);
              if (nextEl) nextEl.focus();
          }, 10);
      } else if (e.key === 'Backspace') {
          if (todoBlocks[index].text === '' && todoBlocks.length > 1) {
              e.preventDefault();
              setTodoBlocks(prev => prev.filter((_, i) => i !== index));
              
              // Find the previous visible block to focus
              const visibleBlocks = todoBlocks.filter(b => {
                  if (todoTab === 'activo') return !b.done && (!b.tab_id || b.tab_id === 'general');
                  if (todoTab === 'hecho') return b.done && (!b.tab_id || b.tab_id === 'general');
                  return b.tab_id === todoTab;
              });
              const visibleIndex = visibleBlocks.findIndex(b => b.id === blockId);
              if (visibleIndex > 0) {
                  setTimeout(() => {
                      const prevEl = document.getElementById(`todo-block-${visibleBlocks[visibleIndex - 1].id}`);
                      if (prevEl) {
                          prevEl.focus();
                          const len = prevEl.value.length;
                          prevEl.setSelectionRange(len, len);
                      }
                  }, 10);
              }
          }
      }
  };

  const toggleBlockDone = (id) => {
      setTodoBlocks(prev => prev.map(b => b.id === id ? { ...b, done: !b.done } : b));
  };
  
  const deleteTodoBlock = (id) => {
      setTodoBlocks(prev => prev.filter(b => b.id !== id));
  };
  
  const handleDragStartBlock = (e, id) => {
      setDraggedBlockId(id);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
  };
  
  const handleDragOverBlock = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDropBlock = (e, targetId) => {
      e.preventDefault();
      if (!draggedBlockId || draggedBlockId === targetId) return;
      
      setTodoBlocks(prev => {
          const draggedIdx = prev.findIndex(b => b.id === draggedBlockId);
          const targetIdx = prev.findIndex(b => b.id === targetId);
          if (draggedIdx === -1 || targetIdx === -1) return prev;
          
          const newBlocks = [...prev];
          const [removed] = newBlocks.splice(draggedIdx, 1);
          newBlocks.splice(targetIdx, 0, removed);
          return newBlocks;
      });
      setDraggedBlockId(null);
  };

  /* Handlers */
  const toggleStatusMenu = (e) => {
    e.stopPropagation();
    setShowStatusMenu(!showStatusMenu);
  };

  const handleStatusChange = (status) => {
    updateUser({ status });
    setShowStatusMenu(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleDapps = (e) => {
    e.stopPropagation();
    setActiveSetting(activeSetting === 'dapps' ? null : 'dapps');
    setShowTodo(false);
  };


  const isAdmin = (() => {
    if (!user) return false;
    // Global role check
    if (user.role === 'admin' || user.role === 'sub-admin') return true;

    // Legacy space-based fallback check just in case
    if (!user.spaces || !user.currentSpace) return false;
    try {
      const sData = typeof user.spaces === 'string' ? JSON.parse(user.spaces) : user.spaces;
      const sObj = sData.spaces || sData;
      let admins = sObj.admin || [];
      if (!Array.isArray(admins)) admins = [admins];
      return admins.includes(user.currentSpace.id);
    } catch (e) {
      return false;
    }
  })();

  return (
    <aside className="menu1" name="menu1">
      <div className="menu1-top" style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div className="profile-container" style={{ position: 'relative', width: 'fit-content', zIndex: 3000 }}>
          <NavLink to="/all/profile" onClick={closePanel}>
            <img className="img-profile" src={user.image} alt="Profile" />
          </NavLink>

          <div
            className={`status-indicator ${user.status ? user.status.toLowerCase() : 'online'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleStatusMenu(e);
            }}
          />

          {showStatusMenu && (
            <div className="status-menu-dropdown">
              <div className="status-option" onClick={() => handleStatusChange('Online')}>
                <span className="status-dot online"></span>
                <span>Online</span>
              </div>
              <div className="status-option" onClick={() => handleStatusChange('Ghost')}>
                <span className="status-dot ghost"></span>
                <span>Ghost</span>
              </div>
              <div className="status-option" onClick={() => handleStatusChange('Busy')}>
                <span className="status-dot busy"></span>
                <span>Busy</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Spaces Switcher (integración de los avances de main) */}
      <div style={{
        position: 'relative',
        width: '45px',
        flexShrink: 0,
        margin: '0 auto 14px auto',
        background: showSpacesDropdown ? '#ffffff' : 'transparent',
        borderRadius: '12px',
        padding: 0,
        transition: 'background 0.2s ease, padding 0.2s ease, margin 0.2s ease',
        zIndex: 2500,
        overflow: 'hidden'
      }}>
        <div
          onClick={() => setShowSpacesDropdown(!showSpacesDropdown)}
          style={{
            width: '100%', 
            height: '45px', 
            flexShrink: 0,
            background: '#000', 
            borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', 
            fontSize: '12px', fontWeight: 'bold', cursor: 'pointer',
            overflow: 'hidden', 
            border: 'none',
            padding: 0,
            margin: 0,
            transition: 'all 0.2s ease'
          }}
          title={user?.currentSpace?.name || t('nav.allSpaces')}
        >
          {user?.currentSpace ? (
            user.currentSpace.icon ? (
              <img src={user.currentSpace.icon} alt="Space" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              user.currentSpace.name ? user.currentSpace.name.substring(0, 2).toUpperCase() : 'SP'
            )
          ) : (
            'ALL'
          )}
        </div>

        {showSpacesDropdown && (
          <div style={{
            width: '100%',
            marginTop: '10px', 
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
            paddingBottom: '10px'
          }}>
            {user?.currentSpace && (
              <div
                style={{
                  width: '38px', height: '38px', borderRadius: '8px',
                  background: 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 'bold', color: '#000', cursor: 'pointer',
                }}
                onClick={() => { 
                  const parts = location.pathname.split('/');
                  if (parts.length > 1 && parts[1] !== '') {
                      parts[1] = 'all';
                      navigate(parts.join('/'));
                  } else {
                      navigate('/all');
                  }
                  setShowSpacesDropdown(false); 
                }}
                title={t('nav.allSpaces')}
              >
                ALL
              </div>
            )}
            {spacesList.filter(s => s.id !== user?.currentSpace?.id).map(s => (
              <div
                key={s.id}
                style={{
                  width: '38px', height: '38px', borderRadius: '8px',
                  background: 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 'bold', color: '#000', cursor: 'pointer',
                  overflow: 'hidden',
                }}
                onClick={() => { 
                  const newSlug = encodeURIComponent(s.name.toLowerCase());
                  const parts = location.pathname.split('/');
                  if (parts.length > 1 && parts[1] !== '') {
                      parts[1] = newSlug;
                      navigate(parts.join('/'));
                  } else {
                      navigate('/' + newSlug);
                  }
                  setShowSpacesDropdown(false); 
                }}
                title={s.name}
              >
                {s.icon ? (
                  <img src={s.icon} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  s.name ? s.name.substring(0, 2).toUpperCase() : 'SP'
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="menu-menu screen" name="menu-menu screen" data-id="19338:2239">
        {(() => {
          const currentSpaceSlug = user?.currentSpace?.name ? encodeURIComponent(user.currentSpace.name.toLowerCase()) : 'all';
          const homePath = `/${currentSpaceSlug}`;

          // El panel de contenido lateral (`.control-menu1` en Home.jsx) no tenía forma de
          // ocultarse: al navegar a una ruta ya activa, NavLink no dispara nada. Igual que en
          // la consola de Google Cloud, volver a presionar el ítem ya activo colapsa el panel;
          // presionar uno distinto lo fuerza a expandirse (para que el contenido nuevo se vea).
          const handleNavClick = (path, isExactMatch) => (e) => {
            const isActive = isExactMatch ? location.pathname === path : location.pathname.startsWith(path);
            const shell = useShellStore.getState();
            if (isActive) shell.toggleMenuCollapsed();
            else shell.setMenuCollapsed(false);
            closePanel(e);
          };

          return (
            <>
              <NavLink to={homePath} onClick={handleNavClick(homePath, true)}>
                <div className="home-TaLwXx" data-id="19338:2244" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Home color="#FFFFFF" size={22} />
                </div>
              </NavLink>

              <NavLink to={`/${currentSpaceSlug}/docs`} onClick={handleNavClick(`/${currentSpaceSlug}/docs`, false)}>
                <div className="doc-TaLwXx" data-id="19338:2243" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <File color="#FFFFFF" size={22} />
                </div>
              </NavLink>

              {permissions?.nav_database !== false && (
                <NavLink to={`/${currentSpaceSlug}/database`} onClick={handleNavClick(`/${currentSpaceSlug}/database`, false)}>
                  <div className="data-TaLwXx" data-id="19338:2242" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Database color="#FFFFFF" size={22} />
                  </div>
                </NavLink>
              )}

              <NavLink to={`/${currentSpaceSlug}/calendar`} onClick={handleNavClick(`/${currentSpaceSlug}/calendar`, false)}>
                <div className="calendar-TaLwXx" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Calendar color="#FFFFFF" size={22} />
                </div>
              </NavLink>

              {permissions?.task_view !== false && (
                <NavLink to={`/${currentSpaceSlug}/task`} onClick={handleNavClick(`/${currentSpaceSlug}/task`, false)}>
                  <div className="task-TaLwXx" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckSquare color="#FFFFFF" size={22} />
                  </div>
                </NavLink>
              )}

              <NavLink to={`/${currentSpaceSlug}/imapmail`} onClick={handleNavClick(`/${currentSpaceSlug}/imapmail`, false)}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '50%', backgroundColor: 'transparent', transition: 'all 0.2s ease', cursor: 'pointer' }} title="Correo">
                  <Mail color="#FFFFFF" size={22} />
                </div>
              </NavLink>

              {(user?.role === 'admin' || user?.role === 'sub-admin' || spaceRole === 'admin' || spaceRole === 'owner') && (
                <NavLink to={`/${currentSpaceSlug}/maptools`} onClick={handleNavClick(`/${currentSpaceSlug}/maptools`, false)}>
                  <div 
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '50%', backgroundColor: 'transparent', transition: 'all 0.2s ease', cursor: 'pointer', marginTop: '4px' }} 
                    title="Editor de Mapa"
                  >
                    <Pickaxe color="#FFFFFF" size={22} />
                  </div>
                </NavLink>
              )}
            </>
          );
        })()}
      </div>

      {/* Todo Button Outside Menu-Menu */}
      
      {/* Todo and Dapps Group */}
      <div className="menu-menu" style={{ marginTop: '0', padding: '9px 0', gap: '14px' }}>
        <div 
          style={{ 
            backgroundColor: showTodo ? '#333' : 'transparent', 
            borderRadius: '50%', 
            width: '38px', height: '38px', 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => {
            setShowTodo(prev => !prev);
            if (!showTodo) setShowWhats(false);
          }}
          title="To-Do"
        >
          <ClipboardList color="#FFFFFF" size={20} />
        </div>
          <div
            className={`nav-item ${showWhats ? 'active' : ''}`}
            style={{ cursor: 'pointer', marginBottom: '14px' }}
            onClick={() => {
              setShowWhats(!showWhats);
              if (!showWhats) setShowTodo(false);
            }}
            title="WhatsApp"
          >
            <MessageCircle color="#FFFFFF" size={20} />
          </div>

        <NavLink
            to={`/${user?.currentSpace?.name ? encodeURIComponent(user.currentSpace.name.toLowerCase()) : 'all'}/dapps`}
            title="DApps e Integraciones"
            style={{ textDecoration: 'none' }}
          >
            <div style={{
              backgroundColor: (window.location.pathname.startsWith('/dapp') || ['/pdfeditor', '/walletsmanager', '/iaatomat', '/docusafe', '/connetgoogle', '/notion', '/imapmail', '/exchange', '/spei', '/facturador', '/boo', '/whats', '/cosmicphone', '/virtualphone', '/jorgebot'].includes(window.location.pathname)) ? '#333' : 'transparent',
              borderRadius: '50%',
              width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}>
              <AppWindow size={20} color="#FFFFFF" />
            </div>
          </NavLink>


      </div>



      <div className="menu-spacer" />

      {/* Todo Panel Over Menu1 */}
      {showTodo && (
        <div className="todo-panel">
          <div className="todo-panel-header">
            <button className="todo-new-block-btn" onClick={() => {
                const targetTab = (todoTab === 'activo' || todoTab === 'hecho') ? 'general' : todoTab;
                const newBlock = { id: Date.now().toString(), text: '', done: false, tab_id: targetTab };
                setTodoBlocks([newBlock, ...todoBlocks]);
                if (todoTab === 'hecho') setTodoTab('activo');
                setTodoSearch('');
                setTimeout(() => {
                    const el = document.getElementById(`todo-block-${newBlock.id}`);
                    if (el) el.focus();
                }, 50);
            }}>
               <Plus size={12} /> nuevo block
            </button>
            <h2 className="todo-panel-title">TODO</h2>
            <button className="todo-close-btn" onClick={() => setShowTodo(false)}>
              <X size={12} color="#000000" />
            </button>
          </div>

          <div className="todo-search-container">
            <input 
                type="text" 
                className="todo-search-input" 
                placeholder="buscar en to-do..." 
                value={todoSearch}
                onChange={(e) => setTodoSearch(e.target.value)}
            />
          </div>

          <div className="todo-panel-tabs">
            <button className={`todo-tab ${todoTab === 'activo' ? 'active' : ''}`} onClick={() => setTodoTab('activo')}>
              activo
              <span className="todo-count-tag">{todoBlocks.filter(b => !b.done && (!b.tab_id || b.tab_id === 'general')).length}</span>
            </button>
            {todoTabs.map(tab => (
              <div key={tab.id} className={`todo-tab custom-tab ${todoTab === tab.id ? 'active' : ''}`} onClick={() => setTodoTab(tab.id)} onDoubleClick={() => setEditingTabId(tab.id)}>
                {editingTabId === tab.id ? (
                    <input 
                        type="text" 
                        autoFocus
                        value={tab.name}
                        onChange={(e) => setTodoTabs(prev => prev.map(t => t.id === tab.id ? { ...t, name: e.target.value } : t))}
                        onBlur={() => setEditingTabId(null)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setEditingTabId(null); }}
                        className="todo-tab-edit-input"
                    />
                ) : (
                    <>
                        <span>{tab.name}</span>
                        <span className="todo-count-tag">{todoBlocks.filter(b => b.tab_id === tab.id).length}</span>
                    </>
                )}
              </div>
            ))}
            <button className={`todo-tab ${todoTab === 'hecho' ? 'active' : ''}`} onClick={() => setTodoTab('hecho')}>
              hecho
              <span className="todo-count-tag">{todoBlocks.filter(b => b.done && (!b.tab_id || b.tab_id === 'general')).length}</span>
            </button>
            <button className="todo-tab-add-btn" onClick={() => {
                const newTabId = Date.now().toString();
                setTodoTabs(prev => [...prev, { id: newTabId, name: 'Nueva Tabla' }]);
                setTodoTab(newTabId);
                setEditingTabId(newTabId);
            }}>
                <Plus size={12} />
            </button>
          </div>
          <div className="todo-panel-content">
            {todoBlocks
              .filter(b => {
                  if (todoTab === 'activo') return !b.done && (!b.tab_id || b.tab_id === 'general');
                  if (todoTab === 'hecho') return b.done && (!b.tab_id || b.tab_id === 'general');
                  return b.tab_id === todoTab;
              })
              .filter(b => (b.text || '').toLowerCase().includes(todoSearch.toLowerCase()))
              .map((block) => (
              <div 
                key={block.id}
                className="todo-block-wrapper"
                draggable
                onDragStart={(e) => handleDragStartBlock(e, block.id)}
                onDragOver={handleDragOverBlock}
                onDrop={(e) => handleDropBlock(e, block.id)}
              >
                {block.done ? (
                  <button className="todo-block-trash-btn" onClick={() => deleteTodoBlock(block.id)} title="Eliminar tarea">
                    <Trash2 size={16} color="#888" />
                  </button>
                ) : (
                  <div 
                    className="todo-block-donut"
                    onClick={() => toggleBlockDone(block.id)}
                  />
                )}
                <div className="todo-block-input-container">
                  <textarea 
                    id={`todo-block-${block.id}`}
                    className={`todo-block-input ${block.done ? 'done' : ''}`}
                    value={block.text}
                    rows={1}
                    onChange={(e) => {
                      handleBlockChange(block.id, e.target.value);
                      adjustTodoHeight(e.target);
                    }}
                    onKeyDown={(e) => handleBlockKeyDown(e, block.id)}
                    onFocus={(e) => {
                      setActiveBlockId(block.id);
                      adjustTodoHeight(e.target);
                    }}
                    onBlur={(e) => {
                      // Prevent immediate close to allow clicking icons
                      if (!e.currentTarget.parentNode.contains(e.relatedTarget)) {
                        setActiveBlockId(null);
                      }
                    }}
                    placeholder="Escribe un pendiente..."
                    autoComplete="off"
                  />
                  {activeBlockId === block.id && (
                    <div className="todo-block-actions" tabIndex="-1">
                      <button className="todo-action-btn" title="Volver tarea"><CheckSquare size={14} /></button>
                      <button className="todo-action-btn" title="Recordatorio"><Bell size={14} /></button>
                      <button className="todo-action-btn" title="WhatsApp"><MessageCircle size={14} /></button>
                      <button className="todo-action-btn" title="Mail"><Mail size={14} /></button>
                      <button className="todo-action-btn" title="Evento"><Calendar size={14} /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showWhats && <WhatsPanel user={user} onClose={() => {
        setShowWhats(false);
        if (window.location.hash.startsWith('#whats')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }} />}


      <div className="menu1-bottom">

        

        <button className="nav-item logout-btn" onClick={handleLogout} title={t('nav.logout')}>
          <Power size={24} color="#B13535" />
        </button>
      </div>
          </aside>
  );
};

export default Menu1;
